import React, { useState } from 'react'
import s from '@commonStyles/footer/Footer.module.scss'
import cn from 'classnames'
import { Logo } from '@components/brand'
import { TFunction, useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { useRouter } from 'next/router'
import PreFooter from '@components/common/PreFooter'
import { useIsTabletScreen } from '@lib/utils/mediaQuery'
import { Newsletter } from '@corratech/pylot-footer-newsletter'
import { Accordion } from '@corratech/pylot-ui'
import {
    Bolivia,
    Chile,
    Colombia,
    Facebook,
    Instagram,
    Linkedin,
    Mexico,
    Peru,
    Pinterest,
    Tiktok,
    Youtube
} from './icons'
import {
    FOOTER_COLUMN_SECTIONS,
    FOOTER_COUNTRY_LINKS,
    FOOTER_PAYMENT_SLOTS,
    FOOTER_SOCIAL_LINKS,
    FOOTER_URLS,
    FooterNavLink
} from './footer-config'
import type {
    FooterCountryLink,
    FooterSocialNetwork
} from './footer-config/types'

const SOCIAL_ICONS: Record<
    FooterSocialNetwork,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
    instagram: Instagram,
    facebook: Facebook,
    pinterest: Pinterest,
    linkedin: Linkedin,
    tiktok: Tiktok,
    youtube: Youtube
}

const COUNTRY_ICONS: Record<
    FooterCountryLink['country'],
    React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
    chile: Chile,
    peru: Peru,
    colombia: Colombia,
    bolivia: Bolivia,
    mexico: Mexico
}

function renderFooterTagline(text: string, styles: typeof s): React.ReactNode {
    const afterPensados = text.indexOf('pensados')
    const afterAcompanar = text.indexOf('acompañar')
    const afterMomentos = text.indexOf('momentos')
    if (
        afterPensados === -1 ||
        afterAcompanar === -1 ||
        afterMomentos === -1 ||
        afterAcompanar < afterPensados ||
        afterMomentos < afterAcompanar
    ) {
        return text
    }

    const splitPensados = afterPensados + 'pensados'.length
    const splitAcompanar = afterAcompanar + 'acompañar'.length
    const splitMomentos = afterMomentos + 'momentos'.length

    return (
        <>
            {text.slice(0, splitPensados)}
            <br className={styles.tagBrAfterPensados} />
            {text.slice(splitPensados, splitAcompanar)}
            <br className={styles.tagBrAfterAcompanar} />
            {text.slice(splitAcompanar, splitMomentos)}
            <br className={styles.tagBrAfterMomentos} />
            {text.slice(splitMomentos)}
        </>
    )
}

function toSentenceCase(value: string): string {
    const trimmed = value.trim()
    if (!trimmed) return value
    const lower = trimmed.toLocaleLowerCase('es-CL')
    return lower.charAt(0).toLocaleUpperCase('es-CL') + lower.slice(1)
}

function renderFooterNavLink(item: FooterNavLink, t: TFunction): JSX.Element {
    const className = s.link
    const label = toSentenceCase(t(item.labelKey))

    if (item.external) {
        return (
            <a
                href={item.href}
                title={item.title}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
            >
                {label}
            </a>
        )
    }

    return (
        <Link href={item.href} title={item.title} className={className}>
            {label}
        </Link>
    )
}

const accordionContent = (t: TFunction, isCompact: boolean) =>
    FOOTER_COLUMN_SECTIONS.map((section) => ({
        id: section.id,
        initialState: section.id === 'servicio',
        Title: function AccordionTitle({ isOpen }: { isOpen: boolean }) {
            const titleKey =
                isCompact && section.titleKeyMobile
                    ? section.titleKeyMobile
                    : section.titleKey
            return (
                <h5 className={cn(s.colTitle, { [s.colTitleOpen]: isOpen })}>
                    {t(titleKey)}
                </h5>
            )
        },
        Content: function AccordionContent() {
            return (
                <div className={s.colLinks}>
                    {section.links.map((link) => (
                        <React.Fragment key={`${link.href}|${link.labelKey}`}>
                            {renderFooterNavLink(link, t)}
                        </React.Fragment>
                    ))}
                </div>
            )
        },
        buttonClass: () => s.accordionButton
    }))

const Footer = (): JSX.Element => {
    const isCompact = useIsTabletScreen()
    const { t } = useTranslation('common')
    const { pathname } = useRouter()
    const isHome = pathname === '/'
    const [showNewsletter, setShowNewsletter] = useState(false)
    const showAppQr = false

    const newsletterBlock = (
        <div className={s.newsletterBlock}>
            <p className={s.newsTitle}>{t('footer|join newsletter')}</p>
            <p className={s.newsCopy}>{t('footer|newsletter teaser')}</p>
            <button
                type="button"
                className={s.inscribe}
                onClick={() => setShowNewsletter((open) => !open)}
                aria-expanded={showNewsletter}
            >
                <span className={s.inscribeLabel}>{t('footer|sign up')}</span>
                <span className={s.inscribeChevron} aria-hidden>
                    {'>'}
                </span>
            </button>
            {showNewsletter ? (
                <div className={s.newsletter}>
                    <Newsletter hideHeading />
                </div>
            ) : null}
        </div>
    )

    const countriesBlock = (
        <div className={s.countries}>
            <p className={s.sectionLabel}>
                {t('footer|find casaideas in your country')}
            </p>
            <ul className={s.countryList}>
                {FOOTER_COUNTRY_LINKS.map((item) => {
                    const Flag = COUNTRY_ICONS[item.country]
                    const isSelected = Boolean(item.selected)
                    return (
                        <li key={item.country}>
                            <a
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={item.title}
                                className={cn(s.countryLink, {
                                    [s.countryLinkSelected]: isSelected
                                })}
                                aria-current={isSelected ? 'true' : undefined}
                            >
                                <Flag />
                            </a>
                        </li>
                    )
                })}
            </ul>
        </div>
    )

    const appDesktop = (
        <div className={s.app}>
            <div className={s.appTexts}>
                <p className={s.sectionLabel}>
                    {t('footer|take us on mobile')}
                </p>
                <p className={s.appCopy}>
                    {t('footer|android qr intro')}{' '}
                    <a href={FOOTER_URLS.iosAppGuide} className={s.inlineLink}>
                        {t('footer|here')}
                    </a>
                    .
                </p>
            </div>
            <img
                src="/footer/app-qr.png"
                alt={t('footer|app qr')}
                className={s.qr}
                width={96}
                height={96}
            />
        </div>
    )

    const appMobile = (
        <div className={s.appMobile}>
            <p className={s.sectionLabel}>{t('footer|take us on mobile')}</p>
            <p className={s.appCopy}>
                {t('footer|android download intro')}{' '}
                <a href={FOOTER_URLS.androidApp} className={s.inlineLink}>
                    {t('footer|here')}
                </a>
                .
            </p>
            <a href={FOOTER_URLS.androidApp} className={s.inscribe}>
                <span className={s.inscribeLabel}>{t('footer|download')}</span>
                <span className={s.inscribeChevron} aria-hidden>
                    {'>'}
                </span>
            </a>
            <p className={s.appCopy}>
                {t('footer|ios indications intro')}{' '}
                <a href={FOOTER_URLS.iosAppGuide} className={s.inlineLink}>
                    {t('footer|here')}
                </a>
                .
            </p>
        </div>
    )

    return (
        <>
            {isHome ? <PreFooter /> : null}
            <footer className={s.root}>
            <div className={s.inner}>
                <div className={s.top}>
                    <div className={s.brand}>
                        <Link
                            href="/"
                            title="Home"
                            className={s.logoLink}
                            aria-label={t('Logo Desktop')}
                        >
                            <Logo className={s.logo} />
                        </Link>
                        <p className={s.tagline}>
                            {renderFooterTagline(t('footer|footer message'), s)}
                        </p>
                        <p className={s.sectionLabel}>
                            {t('footer|follow us')}
                        </p>
                        <ul className={s.socialList}>
                            {FOOTER_SOCIAL_LINKS.map((item) => {
                                const Icon = SOCIAL_ICONS[item.network]
                                return (
                                    <li key={item.network}>
                                        <a
                                            href={item.href}
                                            target={
                                                item.href.startsWith('http')
                                                    ? '_blank'
                                                    : undefined
                                            }
                                            rel={
                                                item.href.startsWith('http')
                                                    ? 'noopener noreferrer'
                                                    : undefined
                                            }
                                            title={item.title}
                                            className={s.socialLink}
                                        >
                                            <Icon />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    {isCompact ? (
                        <div className={s.accordionWrap}>
                            <Accordion
                                className={s.accordion}
                                contents={accordionContent(t, true)}
                            />
                        </div>
                    ) : (
                        <nav className={s.columns} aria-label={t('footer|us')}>
                            {FOOTER_COLUMN_SECTIONS.map((section) => (
                                <div key={section.id} className={s.column}>
                                    <h5 className={s.colTitle}>
                                        {t(section.titleKey)}
                                    </h5>
                                    <div className={s.colLinks}>
                                        {section.links.map((link) => (
                                            <React.Fragment
                                                key={`${link.href}|${link.labelKey}`}
                                            >
                                                {renderFooterNavLink(link, t)}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </nav>
                    )}
                </div>

                <div className={s.mid}>
                    {newsletterBlock}
                    {showAppQr ? (isCompact ? appMobile : appDesktop) : null}
                    {countriesBlock}
                </div>

                <div className={s.bottom}>
                    <div className={s.legal}>
                        <span>{t('footer|copyright')}</span>
                        <span className={s.dot} aria-hidden>
                            |
                        </span>
                        <a
                            href={FOOTER_URLS.termsAndConditions}
                            title="Terminos y Condiciones"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.legalLink}
                        >
                            {t('footer|terms and conditions')}
                        </a>
                        <span className={s.dot} aria-hidden>
                            |
                        </span>
                        <Link
                            href={FOOTER_URLS.privacyPolicy}
                            className={s.legalLink}
                        >
                            {t('footer|privacy policy')}
                        </Link>
                        <span className={s.dot} aria-hidden>
                            |
                        </span>
                        <a
                            href={FOOTER_URLS.officePolicies}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.legalLink}
                        >
                            {t('footer|office policies')}
                        </a>
                    </div>
                    <div className={s.payments}>
                        <span className={s.paymentsLabel}>
                            {t('footer|payment methods')}
                        </span>
                        <ul className={s.paymentList}>
                            {FOOTER_PAYMENT_SLOTS.map((item) => (
                                <li key={item.id}>
                                    <img
                                        src={item.src}
                                        alt={item.label}
                                        width={item.width}
                                        height={item.height}
                                        className={s.paymentLogo}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            </footer>
        </>
    )
}

export default Footer
